// Visor inline de un documento firmado (pedido explícito de Sue 2026-09-27,
// pestaña "Historia") — mismo patrón de PanelFirmaImpresion.tsx ("Ver/imprimir"
// en un <iframe>, nunca window.open/descarga automática: un blob: se bloquea
// en la mayoría de navegadores como pop-up si se abre con window.open()).
import type { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { notify } from '@/components/ui/sonner';
import { tituloDocumentoFirmado } from '@/lib/documentosFirmados';

export interface VisorDocumentoFirmadoProps {
  readonly documento: DocumentReference | null;
  readonly onCerrar: () => void;
}

export function VisorDocumentoFirmado({ documento, onCerrar }: VisorDocumentoFirmadoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documento) {
      setUrl(null);
      setError(null);
      return;
    }
    let cancelado = false;
    let objectUrl: string | null = null;
    const adjuntoUrl = documento.content?.[0]?.attachment?.url;
    if (!adjuntoUrl) {
      setError(t('fichaPaciente.documentoSinAdjunto'));
      return;
    }
    medplum
      .download(adjuntoUrl)
      .then((blob) => {
        if (cancelado) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (!cancelado) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelado = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [medplum, documento, t]);

  if (error) notify({ color: 'red', message: error });

  return (
    <Dialog open={documento !== null} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="max-h-[90vh] sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{documento ? tituloDocumentoFirmado(documento) : ''}</DialogTitle>
        </DialogHeader>
        {url ? (
          <iframe src={url} title={documento ? tituloDocumentoFirmado(documento) : 'PDF'} className="h-[75vh] w-full rounded-md border border-border" />
        ) : (
          <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
